// Port of Weather/WeatherProvider.swift: the provider interface and WeatherError.

import type { GeoCoordinate, WeatherForecast } from '../core/models';
import {
  describeNetworkError,
  networkErrorFrom,
  NetworkFailure,
  sameNetworkError,
  type NetworkError,
} from '../net/networkError';

/**
 * A source of mountain weather forecasts. Implementations return values already adjusted to `elevation` when given,
 * always stamp `fetchedAt`, flag synthetic output with `isSynthetic`, and reject with `WeatherFailure` only.
 */
export interface WeatherProvider {
  /** Stable identifier (`met-norway`, `demo-synthetic`), for logs and diagnostics. */
  readonly providerID: string;
  forecast(location: GeoCoordinate, elevation?: number): Promise<WeatherForecast>;
}

/** Why a forecast could not be obtained. Payloads are developer diagnostics, never UI text. */
export type WeatherError =
  /** The request failed at the transport/HTTP level (offline, timeout, HTTP status…). */
  | { kind: 'network'; error: NetworkError }
  /** The provider answered with a payload that could not be understood. */
  | { kind: 'decoding'; message: string }
  /** No forecast is available (empty answer, invalid location, nothing cached). */
  | { kind: 'noData' };

/** The Error that carries a `WeatherError` when thrown. */
export class WeatherFailure extends Error {
  readonly error: WeatherError;

  constructor(error: WeatherError) {
    super(describeWeatherError(error));
    this.name = 'WeatherFailure';
    this.error = error;
  }
}

export function describeWeatherError(error: WeatherError): string {
  switch (error.kind) {
    case 'network':
      return `network(${describeNetworkError(error.error)})`;
    case 'decoding':
      return `decoding(${error.message})`;
    case 'noData':
      return 'noData';
  }
}

export function sameWeatherError(a: WeatherError, b: WeatherError): boolean {
  if (a.kind === 'network' && b.kind === 'network') return sameNetworkError(a.error, b.error);
  if (a.kind === 'decoding' && b.kind === 'decoding') return a.message === b.message;
  return a.kind === b.kind;
}

/** Maps anything thrown by a provider or HTTP client to a WeatherError (Swift `WeatherError.from`). */
export function weatherErrorFrom(error: unknown): WeatherError {
  if (error instanceof WeatherFailure) return error.error;
  if (error instanceof NetworkFailure) return { kind: 'network', error: error.error };
  if (error instanceof SyntaxError) return { kind: 'decoding', message: error.message };
  return { kind: 'network', error: networkErrorFrom(error) };
}
